import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  ArrowLeft, ArrowRight, CheckCircle, XCircle, 
  Trophy, RotateCcw, BookOpen
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentQuiz = () => {
  const navigate = useNavigate();
  const { quizId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user && user.role !== 'student') {
      navigate('/');
    } else if (user && quizId) {
      fetchQuiz();
    }
  }, [user, authLoading, navigate, quizId]);

  const fetchQuiz = async () => {
    try {
      // Get courses and find the quiz
      const res = await axios.get(`${API}/student/courses`, { withCredentials: true });
      let foundQuiz = null;
      
      for (const course of res.data) {
        for (const module of course.modules || []) {
          if (module.quiz && module.quiz.id === quizId) {
            foundQuiz = {
              ...module.quiz,
              moduleName: module.title,
              courseName: course.title
            };
            break;
          }
        }
        if (foundQuiz) break;
      }
      
      if (foundQuiz) {
        setQuiz(foundQuiz);
      } else {
        toast.error('Quiz not found');
        navigate('/student/courses');
      }
    } catch (error) {
      toast.error('Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, answerIndex) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [questionId]: answerIndex }));
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const unanswered = quiz.questions.filter(q => answers[q.id] === undefined);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }

    try {
      const res = await axios.post(
        `${API}/student/quiz/${quizId}`,
        { answers },
        { withCredentials: true }
      );
      setResult(res.data);
      setSubmitted(true);
      
      if (res.data.passed) {
        toast.success('Congratulations! You passed the quiz!');
      } else {
        toast.error('Keep practicing! You can retake the quiz.');
      }
    } catch (error) {
      toast.error('Failed to submit quiz');
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setCurrentQuestion(0);
    setSubmitted(false);
    setResult(null);
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <p className="text-[#94A3B8]">Quiz not found</p>
      </div>
    );
  }

  // Results View
  if (submitted && result) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-8">
          <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-2xl p-8 text-center">
            <div className={`w-24 h-24 rounded-full mx-auto mb-6 flex items-center justify-center ${
              result.passed ? 'bg-[#00FFE0]/20' : 'bg-[#FF6A00]/20'
            }`}>
              {result.passed ? (
                <Trophy className="w-12 h-12 text-[#00FFE0]" />
              ) : (
                <RotateCcw className="w-12 h-12 text-[#FF6A00]" />
              )}
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-2">
              {result.passed ? 'Congratulations!' : 'Keep Practicing!'}
            </h1>
            <p className="text-[#94A3B8] mb-6">
              {result.passed 
                ? 'You have successfully completed this quiz.' 
                : 'You need 70% to pass. Review the material and try again.'}
            </p>

            <div className="bg-[#060F22]/50 rounded-xl p-6 mb-8">
              <div className="text-6xl font-black mb-2" style={{ color: result.passed ? '#00FFE0' : '#FF6A00' }}>
                {result.score}%
              </div>
              <p className="text-[#94A3B8]">
                {result.correct} out of {result.total} correct
              </p>
            </div>

            {/* Answer Review */}
            <div className="text-left mb-8">
              <h3 className="text-lg font-bold text-white mb-4">Review Answers</h3>
              <div className="space-y-4">
                {quiz.questions.map((q, idx) => {
                  const userAnswer = answers[q.id];
                  const isCorrect = userAnswer === q.correct;
                  return (
                    <div key={q.id} className={`p-4 rounded-xl border ${
                      isCorrect ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'
                    }`}>
                      <div className="flex items-start gap-3">
                        {isCorrect ? (
                          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                          <p className="text-white font-medium mb-2">{idx + 1}. {q.question}</p>
                          <p className="text-sm">
                            <span className="text-[#94A3B8]">Your answer: </span>
                            <span className={isCorrect ? 'text-green-400' : 'text-red-400'}>
                              {q.options[userAnswer]}
                            </span>
                          </p>
                          {!isCorrect && (
                            <p className="text-sm">
                              <span className="text-[#94A3B8]">Correct answer: </span>
                              <span className="text-green-400">{q.options[q.correct]}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              {!result.passed && (
                <button
                  onClick={handleRetake}
                  className="inline-flex items-center gap-2 bg-[#FF6A00] text-white font-bold rounded-xl px-6 py-3"
                >
                  <RotateCcw className="w-5 h-5" />
                  Retake Quiz
                </button>
              )}
              <Link
                to="/student/courses"
                className="inline-flex items-center gap-2 bg-[#00AEEF] text-white font-bold rounded-xl px-6 py-3"
              >
                <BookOpen className="w-5 h-5" />
                Back to Courses
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz View
  const question = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/student/courses" className="text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white">{quiz.title}</h1>
            <p className="text-[#94A3B8] text-sm">{quiz.moduleName}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#94A3B8]">Question {currentQuestion + 1} of {quiz.questions.length}</span>
            <span className="text-sm text-[#00FFE0]">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-[#0A1F44] rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-[#00AEEF] to-[#00FFE0] h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-2xl p-8 mb-6">
          <h2 className="text-xl font-bold text-white mb-6">{question.question}</h2>
          
          <div className="space-y-3">
            {question.options.map((option, idx) => (
              <button
                key={`option-${question.id}-${idx}`}
                onClick={() => handleAnswer(question.id, idx)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  answers[question.id] === idx
                    ? 'border-[#00FFE0] bg-[#00FFE0]/10 text-white'
                    : 'border-[#00AEEF]/20 bg-[#060F22]/50 text-[#94A3B8] hover:border-[#00AEEF]/50 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                    answers[question.id] === idx
                      ? 'bg-[#00FFE0] text-[#060F22]'
                      : 'bg-[#0A1F44] text-[#94A3B8]'
                  }`}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span>{option}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Question Navigation Dots */}
        <div className="flex justify-center gap-2 mb-6">
          {quiz.questions.map((q, idx) => (
            <button
              key={`nav-${q.id}`}
              onClick={() => setCurrentQuestion(idx)}
              className={`w-3 h-3 rounded-full transition-all ${
                idx === currentQuestion
                  ? 'bg-[#00FFE0] w-6'
                  : answers[q.id] !== undefined
                    ? 'bg-[#00AEEF]'
                    : 'bg-[#0A1F44]'
              }`}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={handlePrev}
            disabled={currentQuestion === 0}
            className="inline-flex items-center gap-2 bg-[#0A1F44] text-[#94A3B8] font-bold rounded-xl px-6 py-3 border border-[#00AEEF]/20 disabled:opacity-50 disabled:cursor-not-allowed hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Previous
          </button>

          {currentQuestion === quiz.questions.length - 1 ? (
            <button
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 bg-[#FF6A00] text-white font-bold rounded-xl px-8 py-3 shadow-[0_0_20px_rgba(255,106,0,0.3)] hover:shadow-[0_0_30px_rgba(255,106,0,0.5)] transition-all"
            >
              Submit Quiz
              <CheckCircle className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleNext}
              className="inline-flex items-center gap-2 bg-[#00AEEF] text-white font-bold rounded-xl px-6 py-3 hover:bg-[#00AEEF]/80 transition-colors"
            >
              Next
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentQuiz;
